/**
 * Telestration video overlay — React migration Pass 11. Ports the template's
 * telestration + detection + player-connect subsystems from
 * app/templates/game_match_summary/index.html:
 *   - telestration state + layer toggles (~7697-7806)
 *   - detection boxes overlay (~7793-7935): /api/detection-boxes/<code>
 *   - requestTelestrationData single-flight buffer (~8038-8080):
 *     /api/telestration/<code>?start_frame&end_frame&layers
 *   - homography helpers worldToPixel/pixelToWorld/convexHull/getCurrentMatrix
 *     with the ±6-frame matrix smoothing window (~8082-8180)
 *   - drawTelestration (~8181-8748): offside lines, animated pass arrows,
 *     player connections, auto layers (backline/midfield/shape/space)
 *   - the telLoop rAF hook (~8751) and initPlayerConnect spatial-canvas
 *     click-to-connect (~8789-8901)
 *
 * DEFERRED (excluded from this pass): the QA possession/chain banners
 * (possession_banner / chain_banner layers, ensurePossessionOverlayData,
 * drawPossessionBanner, drawChainBanner, teamLabelFor — template ~7936-8036)
 * and their ?overlays= deep-link entries. The internal-QA buttons for them
 * are not rendered; toggling those layers is impossible until that pass.
 *
 * Button active state is mirrored to React through onTelLayersChanged
 * (the template toggled classes on the DOM buttons directly).
 */
import { videoTimeToFrame } from './frameTime'
import type { CanvasController } from './canvasController'
import { homeIndexOf } from '../../lib/homeIndex'
import { ownGoalSign, pickKeeper } from './shapePanels'

interface TelestrationFrame {
  frame: number
  matrix: Homography
}

interface OffsideLineEntry {
  frame: number
  x: number
  team: number
}

interface PassEntry {
  start_frame: number
  end_frame: number
  from_x: number
  from_y: number
  to_x: number
  to_y: number
  team: number
}

interface TelestrationData {
  frames?: TelestrationFrame[]
  layers?: {
    offside_line?: OffsideLineEntry[]
    passes?: PassEntry[]
  }
}

interface DetectionBox {
  tid: number
  cls: string
  pid: number
  x1: number
  y1: number
  x2: number
  y2: number
}

interface DetectionData {
  frames: Record<string, DetectionBox[]>
}

/**
 * A 3x3 homography, row-major. A TUPLE rather than number[] because it is
 * always exactly nine numbers and saying so is the difference between the
 * compiler knowing `h[8]` exists and having to be told to ignore that it
 * might not. That single type change removed 150 "possibly undefined"
 * errors here without one assertion.
 */
export type Homography = readonly [
  number, number, number,
  number, number, number,
  number, number, number,
]

export interface SelectedPlayer {
  team: number
  jersey: number
  x: number
  y: number
  canvasX: number
  canvasY: number
}

// Project world coordinates (meters) to video pixel coordinates using homography
export function worldToPixel(
  matrix: Homography,
  worldX: number,
  worldY: number,
): { x: number; y: number } | null {
  // matrix is [h00..h22] — maps pixel->world, we need inverse (world->pixel)
  const h = matrix
  // 3x3 matrix inverse
  const det =
    h[0] * (h[4] * h[8] - h[5] * h[7]) -
    h[1] * (h[3] * h[8] - h[5] * h[6]) +
    h[2] * (h[3] * h[7] - h[4] * h[6])
  if (Math.abs(det) < 1e-10) return null
  // Annotated, not inferred: nine literals infer as number[], and the
  // checker then cannot know inv[8] exists.
  const inv: Homography = [
    (h[4] * h[8] - h[5] * h[7]) / det,
    (h[2] * h[7] - h[1] * h[8]) / det,
    (h[1] * h[5] - h[2] * h[4]) / det,
    (h[5] * h[6] - h[3] * h[8]) / det,
    (h[0] * h[8] - h[2] * h[6]) / det,
    (h[2] * h[3] - h[0] * h[5]) / det,
    (h[3] * h[7] - h[4] * h[6]) / det,
    (h[1] * h[6] - h[0] * h[7]) / det,
    (h[0] * h[4] - h[1] * h[3]) / det,
  ]
  const w = inv[6] * worldX + inv[7] * worldY + inv[8]
  if (Math.abs(w) < 1e-10) return null
  return {
    x: (inv[0] * worldX + inv[1] * worldY + inv[2]) / w,
    y: (inv[3] * worldX + inv[4] * worldY + inv[5]) / w,
  }
}

// Inverse of worldToPixel: take a video pixel coord and the current frame's
// homography (which maps pixel→world directly) and return world meters.
// Used by pitch-space drawing tools so a click at frame N becomes a world
// anchor that re-projects correctly when the camera pans at frame N+k.
export function pixelToWorld(
  matrix: Homography,
  px: number,
  py: number,
): { x: number; y: number } | null {
  const h = matrix
  const w = h[6] * px + h[7] * py + h[8]
  if (Math.abs(w) < 1e-10) return null
  return {
    x: (h[0] * px + h[1] * py + h[2]) / w,
    y: (h[3] * px + h[4] * py + h[5]) / w,
  }
}

/**
 * A team's outfield players: the overlay's shape and lines are the
 * outfield's (Ben, 2026-09-21), as in the Team Shape panel. The keeper is
 * the deepest player toward the team's own goal, kept by jersey while on
 * the pitch (pickKeeper). Jerseys 24/25 were the legacy feed's keepers;
 * the stack's keepers wear their own numbers, so that rule alone let them
 * into the shape. With the attack direction unrecorded the own goal is
 * unknown and only the legacy rule is left.
 */
export function outfieldOf<P extends { team: number; jersey: number; x: number }>(
  players: P[], team: 0 | 1, homeStartDirection: string | null | undefined,
  phase: number, prevKeeper: number | null, homeIndex: 0 | 1,
): { outfield: P[]; keeperJersey: number | null } {
  const squad = players.filter((p) => p.team === team)
  const ownSign = ownGoalSign(homeStartDirection, phase, team, homeIndex)
  const keeper = ownSign ? pickKeeper(squad, ownSign, prevKeeper) : null
  return {
    outfield: squad.filter((p) => (keeper ? p !== keeper : ![24, 25].includes(p.jersey))),
    keeperJersey: keeper?.jersey ?? prevKeeper,
  }
}

// Convex hull (Andrew's monotone chain). Input: array of {x, y, ...}.
// Returns the subset that forms the convex hull in counterclockwise order.
export function convexHull<T extends { x: number; y: number }>(points: T[]): T[] {
  if (points.length < 3) return points.slice()
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y)
  const cross = (O: T, A: T, B: T) => (A.x - O.x) * (B.y - O.y) - (A.y - O.y) * (B.x - O.x)

  // One half of the monotone chain. It was written twice, inline, with the
  // `length >= 2` guard proving the two indexes exist — true, and invisible
  // to the compiler. Pulling the pair out with an explicit check satisfies it
  // without an assertion, and removes the duplicate while it is here.
  const chain = (ordered: T[]): T[] => {
    const out: T[] = []
    for (const p of ordered) {
      for (;;) {
        const n = out.length
        if (n < 2) break
        const o = out[n - 2]
        const a = out[n - 1]
        if (o === undefined || a === undefined) break
        if (cross(o, a, p) > 0) break
        out.pop()
      }
      out.push(p)
    }
    return out
  }

  const lower = chain(sorted)
  const upper = chain([...sorted].reverse())

  return lower.slice(0, -1).concat(upper.slice(0, -1))
}

/** Adjacent pairs, as pairs.
 *
 *  Nine drawing routines in this file walked an array with `arr[i]` and
 *  `arr[i + 1]`, which the compiler reads as two possibly-missing lookups even
 *  though the loop bound proves both are there. Producing the pairs once gives
 *  every caller two values it can use directly. The `continue` is unreachable
 *  for a dense array — it is the honest way to say so, and costs a comparison. */
function adjacentPairs<T>(items: readonly T[]): [T, T][] {
  const out: [T, T][] = []
  for (let i = 0; i + 1 < items.length; i++) {
    const a = items[i]
    const b = items[i + 1]
    if (a === undefined || b === undefined) continue
    out.push([a, b])
  }
  return out
}

/** beginPath + moveTo(first) + lineTo(rest) + closePath, for the polygon
 *  outlines this file draws six times. Returns false on an empty array, so the
 *  caller skips its fill rather than filling a path that was never started. */
function tracePolygon(
  ctx: CanvasRenderingContext2D,
  points: readonly { px: number; py: number }[],
): boolean {
  const [first, ...rest] = points
  if (first === undefined) return false
  ctx.beginPath()
  ctx.moveTo(first.px, first.py)
  for (const p of rest) ctx.lineTo(p.px, p.py)
  ctx.closePath()
  return true
}

const DET_REFEREE = { stroke: '#9aa5b1', label: '#e6edf3' }

const DET_COLORS: Record<string, { stroke: string; fill: string; label: string }> = {
  'Team A': { stroke: 'rgba(0, 150, 255, 0.9)', fill: 'rgba(0, 150, 255, 0.15)', label: '#60a5fa' },
  'Team B': { stroke: 'rgba(255, 80, 80, 0.9)', fill: 'rgba(255, 80, 80, 0.15)', label: '#f87171' },
  Goalkeeper: { stroke: 'rgba(255, 200, 0, 0.9)', fill: 'rgba(255, 200, 0, 0.15)', label: '#fbbf24' },
  Referee: { stroke: 'rgba(180, 180, 180, 0.7)', fill: 'rgba(180, 180, 180, 0.1)', label: '#a3a3a3' },
  Ball: { stroke: 'rgba(0, 255, 100, 0.9)', fill: 'rgba(0, 255, 100, 0.2)', label: '#4ade80' },
}

// Smoothing half-window in frames; total averaged = 2*W + 1 (~0.4 sec at
// 30fps) so high-frequency homography noise doesn't jitter drawings.
const SMOOTH_W = 6

const MAX_CONNECTED = 4

export interface DetBoxLike {
  tid: number
  cls: string
  x1: number
  y1: number
  x2: number
  y2: number
}

/** The smallest player box containing (px, py), or null. Smallest wins so
 *  overlapping players resolve to the tighter fit — exact beats arbitrary,
 *  and a second click toggles a wrong pick off. The ball is never selectable
 *  here (it has no detection box; ball highlighting projects the tracked
 *  ball through the homography instead). Pure, for the hit-testing test. */
export function pickSmallestBoxAt(
  boxes: ReadonlyArray<DetBoxLike>,
  px: number, py: number,
): DetBoxLike | null {
  let hit: { box: DetBoxLike; area: number } | null = null
  for (const b of boxes) {
    if (b.cls === 'Ball') continue
    if (px < b.x1 || px > b.x2 || py < b.y1 || py > b.y2) continue
    const area = (b.x2 - b.x1) * (b.y2 - b.y1)
    if (!hit || area < hit.area) hit = { box: b, area }
  }
  return hit ? hit.box : null
}

export class TelestrationController {
  private controller: CanvasController

  telCanvas: HTMLCanvasElement | null = null
  telCtx: CanvasRenderingContext2D | null = null
  telData: TelestrationData | null = null // cached API response
  telActiveLayers: Record<string, boolean> = {} // {offside_line: true, ...}
  /** Each team's keeper jersey, sticky while on the pitch (pickKeeper). */
  private keeperJersey: Record<number, number | null> = {}
  telBufferStart = -1 // frame range currently buffered
  telBufferEnd = -1

  detectionActive = false
  detectionData: DetectionData | null = null
  detectionBufferStart = 0
  detectionBufferEnd = 0
  private _detFetchInFlight = false

  // Single-flight guard: prevents the "fetch storm" where the request fired
  // every animation frame while the first response was still pending.
  private _telFetchInFlight = false

  selectedPlayers: SelectedPlayer[] = [] // player-connect selection

  private rafId: number | null = null
  private spatialCanvas: HTMLCanvasElement | null = null
  private onSpatialMouseMove: ((e: MouseEvent) => void) | null = null
  private onSpatialClick: ((e: MouseEvent) => void) | null = null

  constructor(controller: CanvasController) {
    this.controller = controller
  }

  /** The controller's video, popped out or not (see videoEl). */
  private get video(): HTMLVideoElement | null {
    return this.controller.videoEl()
  }

  /** Player spotlight: 'selecting' while the user clicks players, 'active'
   *  once Done dims everything except the chosen tracks. The map pins each
   *  selected tracker id to its class AT SELECTION TIME: the pipeline's
   *  tracker re-issues ids across identity switches (766 ids in Bradley@
   *  Omaha carry both team classes), so a bare id would follow the label
   *  onto a different player. A class mismatch drops the spotlight instead.
   */
  highlightMode: 'off' | 'selecting' | 'active' = 'off'
  highlightTracks = new Map<number, string>()
  /** Spotlight the tracked ball (projected via homography — the ball has no
   *  detection box). */
  highlightBallOn = false
  /** The boxes from the most recent drawDetectionBoxes call, kept so a
   *  toggle can re-sync the spatial view without waiting for the next frame. */
  private lastDrawnBoxes: DetectionBox[] = []
  /** Draw dashed lines connecting highlighted players on the video. */
  highlightLinesVideo = true
  /** Draw gold rings around highlighted players on the spatial pitch. */
  highlightRingsSpatial = true
  /** Draw dashed lines connecting highlighted players on the spatial pitch. */
  highlightLinesSpatial = true
  private highlightClick = (e: MouseEvent) => this.onHighlightClick(e)

  private notifyLayers(): void {
    this.controller.opts.onTelLayersChanged?.(
      { ...this.telActiveLayers }, this.detectionActive, this.highlightMode,
      this.highlightBallOn, this.highlightLinesVideo, this.highlightRingsSpatial,
      this.highlightLinesSpatial)
  }

  toggleHighlightBall(): void {
    this.highlightBallOn = !this.highlightBallOn
    if (this.highlightBallOn) this.requestTelestrationData()
    this.notifyLayers()
  }

  toggleHighlightLinesVideo(): void {
    this.highlightLinesVideo = !this.highlightLinesVideo
    this.drawDetectionBoxes()
    this.notifyLayers()
  }

  toggleHighlightRingsSpatial(): void {
    this.highlightRingsSpatial = !this.highlightRingsSpatial
    this.syncHighlightToSpatial(this.lastDrawnBoxes)
    this.notifyLayers()
  }

  toggleHighlightLinesSpatial(): void {
    this.highlightLinesSpatial = !this.highlightLinesSpatial
    this.syncHighlightToSpatial(this.lastDrawnBoxes)
    this.notifyLayers()
  }

  startHighlightSelect(): void {
    if (!this.detectionActive) this.toggleDetectionBoxes()
    this.highlightMode = 'selecting'
    if (this.telCanvas) {
      this.telCanvas.style.pointerEvents = 'auto'
      this.telCanvas.style.cursor = 'pointer'
      this.telCanvas.addEventListener('click', this.highlightClick)
    }
    this.notifyLayers()
  }

  finishHighlightSelect(): void {
    this.highlightMode =
      this.highlightTracks.size || this.highlightBallOn ? 'active' : 'off'
    this.endHighlightPointer()
    this.notifyLayers()
  }

  clearHighlight(): void {
    this.highlightTracks.clear()
    this.highlightBallOn = false
    this.highlightMode = 'off'
    this.lastDrawnBoxes = []
    this.endHighlightPointer()
    if (this.telCtx && this.telCanvas) {
      this.telCtx.clearRect(0, 0, this.telCanvas.width, this.telCanvas.height)
    }
    this.controller.setDetectionHighlightPlayers([], true, true)
    this.notifyLayers()
  }

  private endHighlightPointer(): void {
    if (this.telCanvas) {
      this.telCanvas.style.pointerEvents = 'none'
      this.telCanvas.style.cursor = ''
      this.telCanvas.removeEventListener('click', this.highlightClick)
    }
  }

  private onHighlightClick(e: MouseEvent): void {
    if (this.highlightMode !== 'selecting' || !this.telCanvas || !this.detectionData) return
    const video = this.video
    if (!video) return
    const rect = this.telCanvas.getBoundingClientRect()
    // Click in the canvas's CSS box -> the video's native pixels.
    const px = (e.clientX - rect.left) * (this.telCanvas.width / rect.width)
    const py = (e.clientY - rect.top) * (this.telCanvas.height / rect.height)
    const frame = String(videoTimeToFrame(this.controller.getSync(), video.currentTime))
    const boxes = this.detectionData.frames[frame] ?? []
    const hit = pickSmallestBoxAt(boxes, px, py)
    if (hit === null) return
    if (this.highlightTracks.has(hit.tid)) this.highlightTracks.delete(hit.tid)
    else this.highlightTracks.set(hit.tid, hit.cls)
    this.drawDetectionBoxes()
  }

  /** True when this box is a pinned selection: id AND class must match —
   *  the class pin is what stops the spotlight following an id that the
   *  tracker handed to a different (even opposing) player. */
  private isChosen(box: { tid: number; cls: string }): boolean {
    return this.highlightTracks.get(box.tid) === box.cls
  }

  init(): void {
    this.telCanvas = document.getElementById('telestration-canvas') as HTMLCanvasElement | null
    this.telCtx = this.telCanvas ? this.telCanvas.getContext('2d') : null
    this.initPlayerConnect()
    this.startTelLoop()
  }

  destroy(): void {
    if (this.rafId != null) cancelAnimationFrame(this.rafId)
    this.rafId = null
    if (this.spatialCanvas) {
      if (this.onSpatialMouseMove) {
        this.spatialCanvas.removeEventListener('mousemove', this.onSpatialMouseMove)
      }
      if (this.onSpatialClick) {
        this.spatialCanvas.removeEventListener('click', this.onSpatialClick, true)
      }
    }
  }

  // -------------------------------------------------------------------------
  // Layer toggles
  // -------------------------------------------------------------------------

  toggleTelestration(layer: string): void {
    this.telActiveLayers[layer] = !this.telActiveLayers[layer]

    // Clear selected players when toggling connect off
    if (layer === 'connect' && !this.telActiveLayers['connect']) {
      this.selectedPlayers = []
    }

    const anyActive = Object.values(this.telActiveLayers).some((v) => v) || this.detectionActive
    if (this.telCanvas) this.telCanvas.style.display = anyActive ? 'block' : 'none'
    if (anyActive) this.requestTelestrationData()

    // (Template: QA banners preloaded their data here — deferred, see header.)

    this.notifyLayers()
  }

  toggleAutoTelestration(layer: string): void {
    this.telActiveLayers[layer] = !this.telActiveLayers[layer]

    // Auto layers need homography data + telestration canvas visible
    const anyActive = Object.values(this.telActiveLayers).some((v) => v) || this.detectionActive
    if (this.telCanvas) this.telCanvas.style.display = anyActive ? 'block' : 'none'
    if (this.telActiveLayers[layer]) this.requestTelestrationData()

    // Enable formations loading for positional grouping data
    const formations = this.controller.formations
    if (!formations.enabled) {
      formations.enabled = true
    }
    const frame =
      this.controller.state.frames?.[this.controller.state.currentFrameIndex]?.frame || 0
    // The template chained formations.loadFrameFormations(frame) after this,
    // a method that does not exist on FormationsManager — the TypeError was
    // swallowed by .catch(() => {}). Only the ensureLoaded matters.
    formations.ensureLoaded(frame).catch(() => {})

    this.notifyLayers()
  }

  clearTelestration(): void {
    // Turn off all layers
    for (const key of Object.keys(this.telActiveLayers)) {
      this.telActiveLayers[key] = false
    }
    // Turn off detection boxes
    this.detectionActive = false
    // Clear selected players
    this.selectedPlayers = []
    // Clear and hide canvas
    if (this.telCtx && this.telCanvas) {
      this.telCtx.clearRect(0, 0, this.telCanvas.width, this.telCanvas.height)
      this.telCanvas.style.display = 'none'
    }
    this.notifyLayers()
  }

  // -------------------------------------------------------------------------
  // Detection boxes overlay (TRDaaS bounding boxes)
  // -------------------------------------------------------------------------

  toggleDetectionBoxes(): void {
    this.detectionActive = !this.detectionActive
    if (!this.detectionActive && this.highlightMode !== 'off') {
      this.highlightTracks.clear()
      this.highlightMode = 'off'
      this.endHighlightPointer()
    }

    if (this.detectionActive) {
      this.requestDetectionData()
      if (this.telCanvas) this.telCanvas.style.display = 'block'
    } else {
      // Force a redraw to clear detection boxes from canvas.
      // Other active layers will redraw themselves; if none are active,
      // hide the canvas entirely.
      if (this.telCtx && this.telCanvas) {
        this.telCtx.clearRect(0, 0, this.telCanvas.width, this.telCanvas.height)
        if (Object.values(this.telActiveLayers).some((v) => v)) {
          this.drawTelestration()
        } else {
          this.telCanvas.style.display = 'none'
        }
      }
    }
    this.notifyLayers()
  }

  requestDetectionData(): void {
    const video = this.video
    if (!video || !video.duration) return
    if (this._detFetchInFlight) return

    const currentFrame = videoTimeToFrame(this.controller.getSync(), video.currentTime)
    const windowFrames = 750

    // Don't re-fetch if within buffered range
    if (
      this.detectionData &&
      currentFrame >= this.detectionBufferStart + 100 &&
      currentFrame <= this.detectionBufferEnd - 300
    ) {
      return
    }

    const startFrame = Math.max(0, currentFrame - 100)
    const endFrame = startFrame + windowFrames

    this._detFetchInFlight = true
    // Abort a response that never arrives: without this, one hung request
    // (a busy dev server, a dropped connection) left _detFetchInFlight true
    // forever and the overlay died permanently at the window edge.
    const abort = new AbortController()
    const timer = window.setTimeout(() => abort.abort(), 20000)
    fetch(
      `/api/v1/game-match-summary/detection-boxes/${this.controller.gameCode}?start_frame=${startFrame}&end_frame=${endFrame}`,
      { credentials: 'same-origin', signal: abort.signal },
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          this.detectionData = data
          this.detectionBufferStart = startFrame
          this.detectionBufferEnd = endFrame
        }
      })
      .catch((e) => console.warn('Detection data unavailable (will retry):', e))
      .finally(() => {
        window.clearTimeout(timer)
        this._detFetchInFlight = false
      })
  }

  drawDetectionBoxes(): void {
    if (!this.detectionActive || !this.detectionData || !this.telCtx || !this.telCanvas) return
    const video = this.video
    if (!video || !video.duration) return

    this.sizeCanvasToVideo()
    const telCtx = this.telCtx
    const telCanvas = this.telCanvas
    const currentFrame = videoTimeToFrame(this.controller.getSync(), video.currentTime)

    // Refill FIRST: this must run even when the current frame has no boxes,
    // or leaving the buffered window (a seek, a slow fetch, fast playback)
    // kills the overlay permanently — the old tail-call refill sat after the
    // early return below and never fired again.
    this.requestDetectionData()

    const frameStr = String(currentFrame)
    const boxes = this.detectionData.frames[frameStr]
    if (!boxes) return
    this.lastDrawnBoxes = boxes

    // Scale from original video resolution to canvas size
    const scaleX = telCanvas.width / video.videoWidth
    const scaleY = telCanvas.height / video.videoHeight

    // Player spotlight: dim the whole frame, then punch a soft hole over
    // each selected player so they read at full brightness.
    if (this.highlightMode === 'active') {
      telCtx.fillStyle = 'rgba(0,0,0,0.45)'
      telCtx.fillRect(0, 0, telCanvas.width, telCanvas.height)
      telCtx.save()
      telCtx.globalCompositeOperation = 'destination-out'
      const punch = (cx: number, cy: number, rx: number, ry: number) => {
        const glow = telCtx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(rx, ry))
        glow.addColorStop(0, 'rgba(0,0,0,1)')
        glow.addColorStop(0.7, 'rgba(0,0,0,1)')
        glow.addColorStop(1, 'rgba(0,0,0,0)')
        telCtx.fillStyle = glow
        telCtx.beginPath()
        telCtx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2)
        telCtx.fill()
      }
      for (const box of boxes) {
        if (!this.isChosen(box)) continue
        const cx = ((box.x1 + box.x2) / 2) * scaleX
        const cy = ((box.y1 + box.y2) / 2) * scaleY
        punch(cx, cy,
              ((box.x2 - box.x1) / 2) * scaleX * 2.0,
              ((box.y2 - box.y1) / 2) * scaleY * 1.5)
      }
      const ballPx = this.highlightBallOn ? this.projectBall(currentFrame) : null
      if (ballPx) punch(ballPx.x * scaleX, ballPx.y * scaleY,
                        ballPx.r * scaleX, ballPx.r * scaleX)
      telCtx.restore()
      if (ballPx) {
        // A thin ring so the spotlit ball reads as chosen, not just bright.
        telCtx.strokeStyle = '#FFD84D'
        telCtx.lineWidth = 2
        telCtx.beginPath()
        telCtx.arc(ballPx.x * scaleX, ballPx.y * scaleY,
                   ballPx.r * scaleX * 0.45, 0, Math.PI * 2)
        telCtx.stroke()
      }
    }

    // When 2+ players are spotlit and lines are enabled, draw connecting
    // lines between their centers so the viewer reads the group as a unit.
    if (this.highlightMode === 'active' && this.highlightLinesVideo
        && this.highlightTracks.size >= 2) {
      const centers: { x: number; y: number }[] = []
      for (const box of boxes) {
        if (!this.isChosen(box)) continue
        centers.push({
          x: ((box.x1 + box.x2) / 2) * scaleX,
          y: ((box.y1 + box.y2) / 2) * scaleY,
        })
      }
      if (centers.length >= 2) {
        telCtx.save()
        telCtx.strokeStyle = 'rgba(255, 216, 77, 0.7)'
        telCtx.lineWidth = 2
        telCtx.setLineDash([8, 6])
        // Chain every adjacent pair. A full mesh gets unreadable past
        // three players; a chain stays clean.
        for (let i = 0; i < centers.length - 1; i++) {
          telCtx.beginPath()
          telCtx.moveTo(centers[i]!.x, centers[i]!.y)
          telCtx.lineTo(centers[i + 1]!.x, centers[i + 1]!.y)
          telCtx.stroke()
        }
        // Close the loop when 3+ so the shape reads as a polygon.
        if (centers.length >= 3) {
          telCtx.beginPath()
          telCtx.moveTo(centers[centers.length - 1]!.x, centers[centers.length - 1]!.y)
          telCtx.lineTo(centers[0]!.x, centers[0]!.y)
          telCtx.stroke()
        }
        telCtx.restore()
      }
    }

    for (const box of boxes) {
      // Spotlight active: only the chosen players keep their marks; a box
      // on every dimmed player would defeat the dimming.
      if (this.highlightMode === 'active' && !this.isChosen(box)) continue
      // Both sides of the || were index accesses, so the fallback was as
      // possibly-undefined as the thing it was falling back FROM.
      const colors = DET_COLORS[box.cls] ?? DET_REFEREE
      const x = box.x1 * scaleX
      const y = box.y1 * scaleY
      const w = (box.x2 - box.x1) * scaleX
      const h = (box.y2 - box.y1) * scaleY

      // Draw box (stroke only, no fill). Integer-aligned so the 1px-ish
      // stroke doesn't straddle pixels and blur on the CSS downscale.
      const chosen = this.highlightMode !== 'off' && this.isChosen(box)
      telCtx.strokeStyle = chosen ? '#FFD84D' : colors.stroke
      telCtx.lineWidth = chosen ? 3 : 1
      telCtx.strokeRect(Math.round(x) + 0.5, Math.round(y) + 0.5, Math.round(w), Math.round(h))

      // Label (player_id, track id, or "Ball"): small, clean, centered
      // BELOW the player so it never covers the body or the ball flight.
      // Size scales with the canvas's native resolution so it renders the
      // same on-screen size whatever the video's pixel dimensions are.
      const label = box.cls === 'Ball' ? 'Ball' : box.pid >= 0 ? `#${box.pid}` : `T${box.tid}`
      const fontPx = Math.max(9, Math.round(telCanvas.width / 170))
      telCtx.font = `500 ${fontPx}px Inter, "SF Pro Text", system-ui, -apple-system, sans-serif`
      const tw = Math.ceil(telCtx.measureText(label).width)
      const padX = Math.round(fontPx * 0.4)
      const pillW = tw + padX * 2
      const pillH = Math.round(fontPx * 1.5)
      const lx = Math.round(x + w / 2 - pillW / 2)
      const ly = Math.round(y + h) + 2
      telCtx.fillStyle = 'rgba(0,0,0,0.55)'
      if (typeof telCtx.roundRect === 'function') {
        telCtx.beginPath()
        telCtx.roundRect(lx, ly, pillW, pillH, pillH / 2)
        telCtx.fill()
      } else {
        telCtx.fillRect(lx, ly, pillW, pillH)
      }
      telCtx.fillStyle = colors.label
      telCtx.textAlign = 'center'
      telCtx.textBaseline = 'middle'
      telCtx.fillText(label, lx + pillW / 2, ly + pillH / 2 + 0.5)
      telCtx.textAlign = 'left'
    }

    // Bridge: push the highlighted detection identities into the spatial
    // view so `drawPlayer` renders gold rings on the same players on the
    // pitch and `drawHighlightLines` connects them.
    this.syncHighlightToSpatial(boxes)
  }

  /** Map highlighted detection boxes to the spatial view's player identity
   *  format (`"<team>_<jersey>"`) and push the list into the canvas
   *  controller's state.  Team A → 0, Team B → 1; unknown jerseys (pid -1)
   *  are skipped because the spatial view cannot match them. */
  private syncHighlightToSpatial(boxes: DetectionBox[]): void {
    const ids: string[] = []
    if (this.highlightMode === 'active') {
      for (const box of boxes) {
        if (!this.isChosen(box)) continue
        if (box.pid < 0) continue
        const team = box.cls === 'Team A' || box.cls === 'Goalkeeper' ? 0
                   : box.cls === 'Team B' ? 1
                   : null
        if (team === null) continue
        const key = `${team}_${box.pid}`
        if (!ids.includes(key)) ids.push(key)
      }
    }
    this.controller.setDetectionHighlightPlayers(
      ids, this.highlightRingsSpatial, this.highlightLinesSpatial)
  }

  // -------------------------------------------------------------------------
  // Telestration data + homography
  // -------------------------------------------------------------------------

  requestTelestrationData(): void {
    const video = this.video
    if (!video || !video.duration) return
    if (this._telFetchInFlight) return

    const sync = this.controller.getSync()
    const fps = sync.fps
    const gameStartTime = sync.videoGameStartTime || 0
    const currentFrame = Math.round((video.currentTime - gameStartTime) * fps)
    const windowFrames = 750 // 30 seconds

    // Don't re-fetch if we're well inside the buffered range
    if (
      this.telData &&
      currentFrame >= this.telBufferStart + 100 &&
      currentFrame <= this.telBufferEnd - 100
    ) {
      return
    }

    const startFrame = Math.max(0, currentFrame - 100)
    const endFrame = startFrame + windowFrames
    this.telBufferStart = startFrame
    this.telBufferEnd = endFrame

    // Always request at least offside_line to get homography matrices (even
    // if only connect is active)
    const activeLayers = Object.entries(this.telActiveLayers)
      .filter(([, v]) => v)
      .map(([k]) => k)
    const layers = activeLayers.filter((l) => l !== 'connect').join(',') || 'offside_line'

    this._telFetchInFlight = true
    fetch(
      `/api/v1/game-match-summary/telestration/${this.controller.gameCode}?start_frame=${startFrame}&end_frame=${endFrame}&layers=${layers}`,
      { credentials: 'same-origin' },
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          this.telData = data.data
          // Redraw once now that fresh data arrived. Important when the user
          // clicked while paused — telLoop's frame-change check alone wouldn't
          // redraw until the next play/seek.
          if (this.telCanvas && this.telCanvas.style.display !== 'none') {
            this.drawTelestration()
          }
        }
      })
      .catch((e) => console.warn('Telestration data unavailable:', e))
      .finally(() => {
        this._telFetchInFlight = false
      })
  }

  /** The tracked ball at `frame`, projected to VIDEO pixels, with a
   *  spotlight radius derived from a 2m world span so it scales with the
   *  camera. Null when tracking or homography has nothing for the frame. */
  private projectBall(frame: number): { x: number; y: number; r: number } | null {
    const ball = this.controller.trackingCache?.getFrameData(frame)?.ball
    if (!ball) return null
    const matrix = this.getCurrentMatrix()
    if (!matrix) return null
    // Tracking is centimeters; homography world space is meters with Y
    // negated (same un-flip the offside line applies).
    const wx = ball.x / 100
    const wy = -ball.y / 100
    const p = worldToPixel(matrix, wx, wy)
    if (!p) return null
    const edge = worldToPixel(matrix, wx + 1, wy)
    const r = edge ? Math.max(24, Math.hypot(edge.x - p.x, edge.y - p.y)) : 60
    return { x: p.x, y: p.y, r }
  }

  /**
   * Look up the homography for the current playback frame from telData,
   * averaged over a ±SMOOTH_W window of frames to denoise per-frame wobble.
   */
  getCurrentMatrix(): Homography | null {
    if (!this.telData || !this.telData.frames || this.telData.frames.length === 0) return null
    const video = this.video
    const curFrame = video ? videoTimeToFrame(this.controller.getSync(), video.currentTime) : 0
    const frames = this.telData.frames

    // Find the index of the frame closest to (and not after) curFrame
    let centerIdx = 0
    for (let i = 0; i < frames.length; i++) {
      const f = frames[i]
      if (f === undefined) continue
      if (f.frame >= curFrame) {
        centerIdx = i
        break
      }
      centerIdx = i
    }

    // Average matrix entries across the smoothing window
    const start = Math.max(0, centerIdx - SMOOTH_W)
    const end = Math.min(frames.length, centerIdx + SMOOTH_W + 1)
    const sum = [0, 0, 0, 0, 0, 0, 0, 0, 0]
    let n = 0
    for (let i = start; i < end; i++) {
      const m = frames[i]?.matrix
      if (m === undefined) continue
      for (let j = 0; j < 9; j++) sum[j] = (sum[j] ?? 0) + (m[j] ?? 0)
      n++
    }
    if (n === 0) return null
    // Spelled out rather than looped, because the RESULT is a nine-tuple and
    // a loop over an array cannot tell the compiler that.
    return [
      (sum[0] ?? 0) / n, (sum[1] ?? 0) / n, (sum[2] ?? 0) / n,
      (sum[3] ?? 0) / n, (sum[4] ?? 0) / n, (sum[5] ?? 0) / n,
      (sum[6] ?? 0) / n, (sum[7] ?? 0) / n, (sum[8] ?? 0) / n,
    ]
  }

  // -------------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------------

  /** Size the overlay canvas to the video: internal resolution = the
   *  video's native pixels, CSS box = the rendered content area
   *  (object-fit: contain letterboxing). EVERY draw path must call this —
   *  detection-only rendering once skipped it, leaving the canvas at the
   *  300x150 element default and CSS upscaling every box and label into a
   *  giant blur. */
  sizeCanvasToVideo(): void {
    const video = this.video
    const telCanvas = this.telCanvas
    if (!video || !telCanvas) return

    const rect = video.getBoundingClientRect()
    const vidW = video.videoWidth || 1920
    const vidH = video.videoHeight || 1080
    const vidAspect = vidW / vidH
    const containerAspect = rect.width / rect.height

    let renderW: number, renderH: number, offsetX: number, offsetY: number
    if (containerAspect > vidAspect) {
      // Container is wider — video fits height, letterboxed horizontally
      renderH = rect.height
      renderW = rect.height * vidAspect
      offsetX = (rect.width - renderW) / 2
      offsetY = 0
    } else {
      // Container is taller — video fits width, letterboxed vertically
      renderW = rect.width
      renderH = rect.width / vidAspect
      offsetX = 0
      offsetY = (rect.height - renderH) / 2
    }

    // Position canvas to cover only the actual video content area
    telCanvas.style.width = renderW + 'px'
    telCanvas.style.height = renderH + 'px'
    telCanvas.style.left = offsetX + 'px'
    telCanvas.style.top = offsetY + 'px'
    // Reassigning width/height CLEARS a canvas; only touch when stale.
    if (telCanvas.width !== vidW) telCanvas.width = vidW
    if (telCanvas.height !== vidH) telCanvas.height = vidH
  }

  drawTelestration(): void {
    if (!this.telCtx || !this.telData || !this.telCanvas) return
    const video = this.video
    if (!video) return

    const telCtx = this.telCtx
    const telCanvas = this.telCanvas

    this.sizeCanvasToVideo()
    const vidW = telCanvas.width
    const vidH = telCanvas.height

    const sync = this.controller.getSync()
    const fps = sync.fps
    const gameStartTime = sync.videoGameStartTime || 0
    const currentFrame = Math.round((video.currentTime - gameStartTime) * fps)

    telCtx.clearRect(0, 0, vidW, vidH)

    // Find the closest homography matrix for this frame
    const frames = this.telData.frames || []
    let matrix: Homography | null = null
    for (const f of frames) {
      matrix = f.matrix
      if (f.frame >= currentFrame) break
    }
    if (!matrix) return

    // Draw offside line
    if (this.telActiveLayers['offside_line'] && this.telData.layers?.offside_line) {
      const olData = this.telData.layers.offside_line
      for (const ol of olData) {
        if (Math.abs(ol.frame - currentFrame) > 2) continue
        const xMeters = ol.x / 100.0
        // Project points along the pitch width — negate Y to un-flip
        // from V2 coords back to original pipeline coords for homography
        const p1 = worldToPixel(matrix, xMeters, 36)
        const p2 = worldToPixel(matrix, xMeters, -36)
        if (!p1 || !p2) continue
        // Clamp to canvas bounds
        if (p1.x < -500 || p1.x > vidW + 500 || p2.x < -500 || p2.x > vidW + 500) continue

        telCtx.beginPath()
        telCtx.moveTo(p1.x, p1.y)
        telCtx.lineTo(p2.x, p2.y)
        telCtx.strokeStyle = ol.team === 0 ? 'rgba(59, 130, 246, 0.8)' : 'rgba(239, 68, 68, 0.8)'
        telCtx.lineWidth = 3
        telCtx.setLineDash([10, 5])
        telCtx.stroke()
        telCtx.setLineDash([])
      }
    }

    // Draw progressive pass arrows.
    // Pass coords are from V2 tracking (Y-flipped), but homography is in
    // original pipeline space — negate Y to un-flip before projection
    if (this.telActiveLayers['passes'] && this.telData.layers?.passes) {
      for (const pass of this.telData.layers.passes) {
        if (currentFrame < pass.start_frame || currentFrame > pass.end_frame + 15) continue
        const from = worldToPixel(matrix, pass.from_x, -pass.from_y)
        const to = worldToPixel(matrix, pass.to_x, -pass.to_y)
        if (!from || !to) continue

        // Animate: progress from 0 to 1 over the pass duration
        const duration = Math.max(1, pass.end_frame - pass.start_frame)
        const progress = Math.min(1, (currentFrame - pass.start_frame) / duration)
        const curX = from.x + (to.x - from.x) * progress
        const curY = from.y + (to.y - from.y) * progress

        const color = pass.team === 0 ? 'rgba(59, 130, 246, 0.9)' : 'rgba(239, 68, 68, 0.9)'

        // Draw line from passer to current ball position
        telCtx.beginPath()
        telCtx.moveTo(from.x, from.y)
        telCtx.lineTo(curX, curY)
        telCtx.strokeStyle = color
        telCtx.lineWidth = 4
        telCtx.stroke()

        // Passer circle
        telCtx.beginPath()
        telCtx.arc(from.x, from.y, 8, 0, Math.PI * 2)
        telCtx.fillStyle = color
        telCtx.fill()

        // Arrow head at current position
        const angle = Math.atan2(to.y - from.y, to.x - from.x)
        const headLen = 18
        telCtx.beginPath()
        telCtx.moveTo(curX, curY)
        telCtx.lineTo(curX - headLen * Math.cos(angle - 0.4), curY - headLen * Math.sin(angle - 0.4))
        telCtx.moveTo(curX, curY)
        telCtx.lineTo(curX - headLen * Math.cos(angle + 0.4), curY - headLen * Math.sin(angle + 0.4))
        telCtx.lineWidth = 4
        telCtx.stroke()

        // Receiver circle (when pass complete)
        if (progress >= 1) {
          telCtx.beginPath()
          telCtx.arc(to.x, to.y, 8, 0, Math.PI * 2)
          telCtx.strokeStyle = color
          telCtx.lineWidth = 3
          telCtx.stroke()
        }
      }
    }

    // Draw player connections on video (telestrate mode)
    if (this.telActiveLayers['connect'] && this.selectedPlayers.length >= 1) {
      const currentPlayers = this.controller.state.currentPlayers || []
      const projected: (SelectedPlayer & { px: number; py: number; worldX: number; worldY: number })[] = []
      for (const sel of this.selectedPlayers) {
        const match = currentPlayers.find((p) => {
          return p.team === sel.team && p.jersey === sel.jersey
        })
        if (match) {
          const px = worldToPixel(matrix, match.x / 100.0, -match.y / 100.0)
          if (px) {
            projected.push({
              ...sel,
              px: px.x,
              py: px.y,
              worldX: match.x / 100.0,
              worldY: match.y / 100.0,
            })
          }
        }
      }

      const teamColors: Record<number, { line: string; fill: string; bg: string }> = {
        0: { line: 'rgba(59, 130, 246, 0.85)', fill: 'rgba(59, 130, 246, 0.15)', bg: 'rgba(59, 130, 246, 0.9)' },
        1: { line: 'rgba(239, 68, 68, 0.85)', fill: 'rgba(239, 68, 68, 0.15)', bg: 'rgba(239, 68, 68, 0.9)' },
      }
      const mixedColor = { line: 'rgba(255, 215, 0, 0.85)', fill: 'rgba(255, 215, 0, 0.12)', bg: 'rgba(255, 215, 0, 0.9)' }

      // Determine if all same team or mixed
      const teams = new Set(projected.map((p) => p.team))
      const first = projected[0]
      const colors =
        teams.size === 1 && first ? teamColors[first.team] ?? mixedColor : mixedColor

      if (projected.length >= 2) {
        // Sort by world Y so lines connect left-to-right across the pitch
        projected.sort((a, b) => a.worldY - b.worldY)

        // Connect adjacent players in chain (no crisscross)
        telCtx.strokeStyle = colors.line
        telCtx.lineWidth = 3
        telCtx.setLineDash([8, 4])

        for (const [a, b] of adjacentPairs(projected)) {
          telCtx.beginPath()
          telCtx.moveTo(a.px, a.py)
          telCtx.lineTo(b.px, b.py)
          telCtx.stroke()

          // Distance label (meters) at midpoint
          const dist = Math.sqrt((a.worldX - b.worldX) ** 2 + (a.worldY - b.worldY) ** 2)
          const midX = (a.px + b.px) / 2
          const midY = (a.py + b.py) / 2 - 8
          const label = dist.toFixed(1) + 'm'

          telCtx.font = 'bold 22px Arial'
          const textW = telCtx.measureText(label).width
          // Background pill
          telCtx.fillStyle = 'rgba(0, 0, 0, 0.7)'
          telCtx.beginPath()
          telCtx.roundRect(midX - textW / 2 - 6, midY - 13, textW + 12, 26, 4)
          telCtx.fill()
          // Text
          telCtx.fillStyle = 'white'
          telCtx.textAlign = 'center'
          telCtx.textBaseline = 'middle'
          telCtx.fillText(label, midX, midY)
        }
        telCtx.setLineDash([])

        // Fill polygon along the chain if 3+
        if (projected.length >= 3 && tracePolygon(telCtx, projected)) {
          telCtx.fillStyle = colors.fill
          telCtx.fill()
        }
      }

      // Player circles with jersey number labels
      for (const p of projected) {
        const pColor = teamColors[p.team] || mixedColor
        // Outer ring
        telCtx.beginPath()
        telCtx.arc(p.px, p.py, 18, 0, Math.PI * 2)
        telCtx.strokeStyle = pColor.line
        telCtx.lineWidth = 3
        telCtx.setLineDash([])
        telCtx.stroke()
        // Filled circle
        telCtx.beginPath()
        telCtx.arc(p.px, p.py, 14, 0, Math.PI * 2)
        telCtx.fillStyle = pColor.bg
        telCtx.fill()
        // Jersey number
        telCtx.fillStyle = 'white'
        telCtx.font = 'bold 16px Arial'
        telCtx.textAlign = 'center'
        telCtx.textBaseline = 'middle'
        telCtx.fillText(String(p.jersey || '?'), p.px, p.py)
      }
    }

    // ---- Auto-telestration: positional groups on video ----
    this.drawAutoLayers(matrix)

    // (Template: QA possession/chain banners drew here — deferred.)

    // Re-fetch data if nearing buffer edge
    if (currentFrame > this.telBufferEnd - 200 || currentFrame < this.telBufferStart + 50) {
      this.requestTelestrationData()
    }
  }

  private drawAutoLayers(matrix: Homography): void {
    const telCtx = this.telCtx!
    const autoLayers = ['backline', 'midfield', 'shape', 'space']
    if (!autoLayers.some((l) => this.telActiveLayers[l])) return

    const players = this.controller.state.currentPlayers || []
    const viewTeam = this.controller.opts.getViewingAsTeam()

    const team = viewTeam === 0 ? 0 : 1
    const { outfield, keeperJersey } = outfieldOf(
      players, team,
      this.controller.opts.getGameData()?.home_team_start_direction,
      this.controller.state.frames?.[this.controller.state.currentFrameIndex]?.phase ?? 1,
      this.keeperJersey[team] ?? null,
      homeIndexOf(this.controller.opts.getGameData()?.identity))
    this.keeperJersey[team] = keeperJersey
    const teamPlayers = outfield
      .map((p) => ({ ...p, worldX: p.x / 100.0, worldY: p.y / 100.0 }))

    type WorldPlayer = (typeof teamPlayers)[number]

    if (teamPlayers.length < 4) return

    // Use formation data to classify players into positional groups
    const currentFrameData =
      this.controller.state.frames?.[this.controller.state.currentFrameIndex]
    const formationStr =
      viewTeam === 0 ? currentFrameData?.formation_team_0 : currentFrameData?.formation_team_1
    const assignments =
      viewTeam === 0 ? currentFrameData?.assignments_team_0 : currentFrameData?.assignments_team_1

    let backLine: WorldPlayer[] = []
    let midLine: WorldPlayer[] = []
    let frontLine: WorldPlayer[] = []

    if (formationStr && assignments && assignments.length > 0) {
      // Parse formation string into group sizes: "31222" → [3, 1, 2, 2, 2]
      const groups = formationStr
        .split('')
        .map(Number)
        .filter((n) => !isNaN(n) && n > 0)
      let idx = 0

      // Assignments are ordered FRONT to BACK:
      // First group = front line (attackers), last group = back line (defenders)
      const allGroups: WorldPlayer[][] = []
      for (const size of groups) {
        const jerseys = assignments.slice(idx, idx + size)
        const groupPlayers = jerseys
          .map((j) => teamPlayers.find((p) => p.jersey === j))
          .filter((p): p is WorldPlayer => Boolean(p))
        allGroups.push(groupPlayers)
        idx += size
      }

      const firstGroup = allGroups[0]
      const lastGroup = allGroups[allGroups.length - 1]
      if (firstGroup && lastGroup && allGroups.length >= 2) {
        frontLine = firstGroup
        backLine = lastGroup
        // Everything in between is midfield
        midLine = midLine.concat(...allGroups.slice(1, -1))
      }
    } else {
      // Fallback: sort by X position if no formation data
      const sorted = [...teamPlayers].sort((a, b) => a.worldX - b.worldX)
      const n = sorted.length
      const backCount = Math.max(2, Math.round(n * 0.4))
      const frontCount = Math.max(1, Math.round(n * 0.2))
      backLine = sorted.slice(0, backCount)
      midLine = sorted.slice(backCount, n - frontCount)
      frontLine = sorted.slice(n - frontCount)
    }
    // frontLine is computed for parity with the template, which also never
    // drew it (no front-line layer exists).
    void frontLine

    const teamColor =
      viewTeam === 0
        ? { line: 'rgba(59, 130, 246, 0.85)', fill: 'rgba(59, 130, 246, 0.12)' }
        : { line: 'rgba(239, 68, 68, 0.85)', fill: 'rgba(239, 68, 68, 0.12)' }

    const drawLine = (group: WorldPlayer[], color: string, label: string) => {
      // Sort by Y for clean left-to-right chain
      const sorted = [...group].sort((a, b) => a.worldY - b.worldY)
      const projected = sorted
        .map((p) => {
          const px = worldToPixel(matrix, p.worldX, -p.worldY)
          return px ? { ...p, px: px.x, py: px.y } : null
        })
        .filter((p): p is WorldPlayer & { px: number; py: number } => p !== null)

      if (projected.length < 2) return projected

      // Draw connecting lines
      telCtx.strokeStyle = color
      telCtx.lineWidth = 3
      telCtx.setLineDash([8, 4])
      for (const [a, b] of adjacentPairs(projected)) {
        telCtx.beginPath()
        telCtx.moveTo(a.px, a.py)
        telCtx.lineTo(b.px, b.py)
        telCtx.stroke()

        // Distance label
        const dist = Math.sqrt((a.worldX - b.worldX) ** 2 + (a.worldY - b.worldY) ** 2)
        const mx = (a.px + b.px) / 2
        const my = (a.py + b.py) / 2 - 8
        const lbl = dist.toFixed(1) + 'm'
        telCtx.font = 'bold 18px Arial'
        const tw = telCtx.measureText(lbl).width
        telCtx.fillStyle = 'rgba(0,0,0,0.7)'
        telCtx.setLineDash([])
        telCtx.beginPath()
        telCtx.roundRect(mx - tw / 2 - 4, my - 10, tw + 8, 20, 3)
        telCtx.fill()
        telCtx.fillStyle = 'white'
        telCtx.textAlign = 'center'
        telCtx.textBaseline = 'middle'
        telCtx.fillText(lbl, mx, my)
        telCtx.setLineDash([8, 4])
      }
      telCtx.setLineDash([])

      // Player dots with jersey
      for (const p of projected) {
        telCtx.beginPath()
        telCtx.arc(p.px, p.py, 14, 0, Math.PI * 2)
        telCtx.fillStyle = color
        telCtx.fill()
        telCtx.fillStyle = 'white'
        telCtx.font = 'bold 14px Arial'
        telCtx.textAlign = 'center'
        telCtx.textBaseline = 'middle'
        telCtx.fillText(String(p.jersey || '?'), p.px, p.py)
      }

      // Total span label
      const spanStart = projected[0]
      const spanEnd = projected[projected.length - 1]
      const spanMid = projected[Math.floor(projected.length / 2)]
      if (spanStart && spanEnd && spanMid && projected.length >= 2 && label) {
        const totalDist = Math.sqrt(
          (spanStart.worldX - spanEnd.worldX) ** 2 + (spanStart.worldY - spanEnd.worldY) ** 2,
        )
        const topX = spanMid.px
        const topY = Math.min(...projected.map((p) => p.py)) - 25
        const spanLbl = `${label}: ${totalDist.toFixed(1)}m`
        telCtx.font = 'bold 20px Arial'
        const stw = telCtx.measureText(spanLbl).width
        telCtx.fillStyle = 'rgba(0,0,0,0.8)'
        telCtx.beginPath()
        telCtx.roundRect(topX - stw / 2 - 8, topY - 12, stw + 16, 28, 4)
        telCtx.fill()
        telCtx.fillStyle = '#facc15'
        telCtx.textAlign = 'center'
        telCtx.textBaseline = 'middle'
        telCtx.fillText(spanLbl, topX, topY + 2)
      }

      return projected
    }

    // Draw requested layers
    if (this.telActiveLayers['backline']) {
      drawLine(backLine, teamColor.line, 'Back Line')
    }
    if (this.telActiveLayers['midfield']) {
      drawLine(midLine, teamColor.line, 'Midfield')
    }
    if (this.telActiveLayers['shape']) {
      // Convex hull around field players — the team's footprint on the pitch.
      // Drop 2σ outliers first: in soccer a team rarely spans >30m wide /
      // >25m long instantaneously, so a player far from the centroid is
      // almost always a tracking artifact, and the hull is very sensitive to
      // a single outlier.
      if (teamPlayers.length >= 3) {
        const xs = teamPlayers.map((p) => p.worldX)
        const ys = teamPlayers.map((p) => p.worldY)
        const meanX = xs.reduce((a, b) => a + b, 0) / xs.length
        const meanY = ys.reduce((a, b) => a + b, 0) / ys.length
        const stdX = Math.sqrt(xs.reduce((s, v) => s + (v - meanX) ** 2, 0) / xs.length)
        const stdY = Math.sqrt(ys.reduce((s, v) => s + (v - meanY) ** 2, 0) / ys.length)
        // Floor std at a reasonable team-spread minimum so we don't
        // aggressively trim players from a tightly-clustered formation.
        const cutX = Math.max(2 * stdX, 12)
        const cutY = Math.max(2 * stdY, 10)
        const filtered = teamPlayers.filter(
          (p) => Math.abs(p.worldX - meanX) <= cutX && Math.abs(p.worldY - meanY) <= cutY,
        )
        const hullSrc = filtered.length >= 3 ? filtered : teamPlayers

        // Hull in world coords (Y negated at projection time)
        const hullPts = hullSrc.map((p) => ({ x: p.worldX, y: p.worldY, jersey: p.jersey }))
        const hull = convexHull(hullPts)

        const projected = hull
          .map((p) => {
            const px = worldToPixel(matrix, p.x, -p.y)
            return px ? { ...p, px: px.x, py: px.y } : null
          })
          .filter((p): p is { x: number; y: number; jersey: number; px: number; py: number } => p !== null)

        if (projected.length >= 3 && tracePolygon(telCtx, projected)) {
          // Filled polygon
          telCtx.fillStyle = teamColor.fill
          telCtx.fill()

          // Dashed outline
          telCtx.strokeStyle = teamColor.line
          telCtx.lineWidth = 2.5
          telCtx.setLineDash([8, 4])
          telCtx.stroke()
          telCtx.setLineDash([])

          // Area in world m² via the shoelace formula on the world-coord hull
          let area2 = 0
          for (const [a, b] of adjacentPairs([...hull, ...hull.slice(0, 1)])) {
            area2 += a.x * b.y - b.x * a.y
          }
          const area = Math.abs(area2) / 2

          // Centroid label "Team area: 678 m²"
          const cx = projected.reduce((s, p) => s + p.px, 0) / projected.length
          const cy = projected.reduce((s, p) => s + p.py, 0) / projected.length
          const lbl = `Team area: ${area.toFixed(0)} m²`
          telCtx.font = 'bold 20px Arial'
          const tw = telCtx.measureText(lbl).width
          telCtx.fillStyle = 'rgba(0, 0, 0, 0.8)'
          telCtx.beginPath()
          telCtx.roundRect(cx - tw / 2 - 8, cy - 12, tw + 16, 28, 4)
          telCtx.fill()
          telCtx.fillStyle = '#facc15'
          telCtx.textAlign = 'center'
          telCtx.textBaseline = 'middle'
          telCtx.fillText(lbl, cx, cy + 2)
        }
      }
    }
    if (this.telActiveLayers['space']) {
      // Highlight gaps between adjacent defenders in the back line. Two
      // defenders standing too far apart create a "channel" an attacker can
      // run through: sort the back line laterally (by worldY), measure the
      // distance between adjacent pairs, and shade any gap above threshold.
      if (backLine.length >= 2) {
        const sortedDefs = [...backLine].sort((a, b) => a.worldY - b.worldY)
        const projected = sortedDefs
          .map((p) => {
            const px = worldToPixel(matrix, p.worldX, -p.worldY)
            return px ? { ...p, px: px.x, py: px.y } : null
          })
          .filter((p): p is WorldPlayer & { px: number; py: number } => p !== null)

        const GAP_THRESHOLD = 12 // meters — gaps above this draw a band
        const GAP_RED = 20 // meters — gaps at or above this go full red
        const BAND_HALF_WIDTH = 3 // meters perpendicular to defender line

        if (projected.length >= 2) {
          for (const [a, b] of adjacentPairs(projected)) {
            const dx = b.worldX - a.worldX
            const dy = b.worldY - a.worldY
            const dist = Math.sqrt(dx * dx + dy * dy)
            if (dist <= GAP_THRESHOLD) continue

            // Severity in [0, 1]
            const severity = Math.min(1, (dist - GAP_THRESHOLD) / (GAP_RED - GAP_THRESHOLD))

            // Color graduated from amber (251,191,36) to red (239,68,68)
            const cr = Math.round(251 + (239 - 251) * severity)
            const cg = Math.round(191 + (68 - 191) * severity)
            const cb = Math.round(36 + (68 - 36) * severity)
            const fillColor = `rgba(${cr}, ${cg}, ${cb}, 0.4)`
            const lineColor = `rgba(${cr}, ${cg}, ${cb}, 0.95)`

            // Perpendicular direction in world coords
            const ux = dx / dist,
              uy = dy / dist
            const px = -uy,
              py = ux // 90° rotation
            // Tuples, not number[]: destructuring `[wx, wy]` out of a plain
            // array yields two possibly-missing numbers.
            const worldCorners: [number, number][] = [
              [a.worldX + px * BAND_HALF_WIDTH, a.worldY + py * BAND_HALF_WIDTH],
              [b.worldX + px * BAND_HALF_WIDTH, b.worldY + py * BAND_HALF_WIDTH],
              [b.worldX - px * BAND_HALF_WIDTH, b.worldY - py * BAND_HALF_WIDTH],
              [a.worldX - px * BAND_HALF_WIDTH, a.worldY - py * BAND_HALF_WIDTH],
            ]
            const cs = worldCorners
              .map(([wx, wy]) => worldToPixel(matrix, wx, -wy))
              .filter((c): c is { x: number; y: number } => c !== null)
              .map((c) => ({ px: c.x, py: c.y }))

            // All four projected, or the band is not drawn — the same test the
            // `corners.every(Boolean)` here used to make, without the cast.
            if (cs.length === 4 && tracePolygon(telCtx, cs)) {
              telCtx.fillStyle = fillColor
              telCtx.fill()
              telCtx.strokeStyle = lineColor
              telCtx.lineWidth = 2
              telCtx.setLineDash([6, 4])
              telCtx.stroke()
              telCtx.setLineDash([])
            }

            // Distance label at midpoint
            const midPx = (a.px + b.px) / 2
            const midPy = (a.py + b.py) / 2
            const lbl = `${dist.toFixed(1)} m`
            telCtx.font = 'bold 18px Arial'
            const tw = telCtx.measureText(lbl).width
            telCtx.fillStyle = 'rgba(0, 0, 0, 0.85)'
            telCtx.beginPath()
            telCtx.roundRect(midPx - tw / 2 - 6, midPy - 11, tw + 12, 22, 3)
            telCtx.fill()
            telCtx.fillStyle = severity > 0.5 ? '#ef4444' : '#facc15'
            telCtx.textAlign = 'center'
            telCtx.textBaseline = 'middle'
            telCtx.fillText(lbl, midPx, midPy + 1)
          }
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // rAF hook (template initTelestration telLoop)
  // -------------------------------------------------------------------------

  private startTelLoop(): void {
    const video = this.video
    if (!video || !this.telCanvas) return

    // Draw on each animation frame (works while playing or paused/seeking)
    let lastTelFrame = -1
    const telLoop = () => {
      const anyTelActive = Object.values(this.telActiveLayers).some((v) => v)
      const anyOverlayActive = anyTelActive || this.detectionActive || this.highlightMode !== 'off'
      if (this.highlightBallOn && this.highlightMode !== 'off') this.requestTelestrationData()
      if (anyOverlayActive) {
        const curFrame = videoTimeToFrame(this.controller.getSync(), video.currentTime)
        // Redraw if frame changed or video is playing
        if (!video.paused || curFrame !== lastTelFrame) {
          // drawTelestration clears the canvas internally; if only detection
          // is active we need to clear manually.
          if (anyTelActive) {
            this.drawTelestration()
          } else if (this.telCtx && this.telCanvas) {
            this.telCtx.clearRect(0, 0, this.telCanvas.width, this.telCanvas.height)
          }
          this.drawDetectionBoxes()
          lastTelFrame = curFrame
        }
        if (anyTelActive) this.requestTelestrationData()
      }
      this.rafId = requestAnimationFrame(telLoop)
    }
    this.rafId = requestAnimationFrame(telLoop)
  }

  // -------------------------------------------------------------------------
  // Player connection — click players on spatial pitch to connect
  // (template initPlayerConnect, ~8789)
  // -------------------------------------------------------------------------

  /** Idempotently enable/disable telestrate "connect" overlay on the video. */
  setConnectMode(on: boolean): void {
    if (this.telActiveLayers['connect'] === on) return
    this.telActiveLayers['connect'] = on
    const anyActive = Object.values(this.telActiveLayers).some((v) => v) || this.detectionActive
    if (this.telCanvas) this.telCanvas.style.display = anyActive ? 'block' : 'none'
    if (on) this.requestTelestrationData()
    this.notifyLayers()
  }

  private initPlayerConnect(): void {
    const spatialCanvas = document.getElementById('spatial-canvas') as HTMLCanvasElement | null
    if (!spatialCanvas) return
    this.spatialCanvas = spatialCanvas

    // True when the current game has telestration data (homography in R2).
    // Works even in annotation mode (which hides the controls but should not
    // block spatial-click → video).
    const telestrationAvailable = () => this.controller.hasTelestration

    // Hover: show pointer cursor when near a player. Only when telestration is
    // available for this game (otherwise clicks won't project to anything).
    this.onSpatialMouseMove = (e: MouseEvent) => {
      if (!telestrationAvailable()) {
        spatialCanvas.style.cursor = ''
        return
      }
      const rect = spatialCanvas.getBoundingClientRect()
      const scaleX = spatialCanvas.width / rect.width
      const scaleY = spatialCanvas.height / rect.height
      const mx = (e.clientX - rect.left) * scaleX
      const my = (e.clientY - rect.top) * scaleY
      const players = this.controller.state.currentPlayers || []
      let nearPlayer = false
      for (const p of players) {
        if (p.team !== 0 && p.team !== 1) continue
        const pos = this.controller.convertToCanvasCoords(p.x, p.y)
        if (Math.sqrt((pos.x - mx) ** 2 + (pos.y - my) ** 2) < 20) {
          nearPlayer = true
          break
        }
      }
      spatialCanvas.style.cursor = nearPlayer ? 'pointer' : ''
    }
    spatialCanvas.addEventListener('mousemove', this.onSpatialMouseMove)

    // One-shot redraw after a click so feedback is immediate even while
    // paused. Cheap: a single drawFrame for the gold ring + a single
    // drawTelestration for the video circle (only if data is already loaded;
    // otherwise the requestTelestrationData success handler redraws).
    const redrawAfterClick = () => {
      const s = this.controller.state
      const frame = s.frames?.[s.currentFrameIndex]
      if (frame) this.controller.drawFrame(frame)
      if (this.telData && this.telCanvas && this.telCanvas.style.display !== 'none') {
        this.drawTelestration()
      }
    }

    this.onSpatialClick = (e: MouseEvent) => {
      // No-op on games without homography (telestration unavailable)
      if (!telestrationAvailable()) return

      // Stop event from reaching spatial canvas scrub/seek handlers
      e.stopPropagation()
      e.preventDefault()

      const rect = spatialCanvas.getBoundingClientRect()
      const scaleX = spatialCanvas.width / rect.width
      const scaleY = spatialCanvas.height / rect.height
      const clickX = (e.clientX - rect.left) * scaleX
      const clickY = (e.clientY - rect.top) * scaleY

      // Find the closest player to the click
      const players = this.controller.state.currentPlayers || []
      let closest: SelectedPlayer | null = null
      let closestDist = Infinity

      for (const p of players) {
        if (p.team !== 0 && p.team !== 1) continue
        const pos = this.controller.convertToCanvasCoords(p.x, p.y)
        const dist = Math.sqrt((pos.x - clickX) ** 2 + (pos.y - clickY) ** 2)
        if (dist < closestDist && dist < 25) {
          closest = { team: p.team, jersey: p.jersey, x: p.x, y: p.y, canvasX: pos.x, canvasY: pos.y }
          closestDist = dist
        }
      }

      if (!closest) {
        // Clicked empty space — clear selection and disable overlay
        this.selectedPlayers = []
        this.setConnectMode(false)
        redrawAfterClick()
        return
      }

      // Check if already selected — toggle off
      const idx = this.selectedPlayers.findIndex(
        (s) => s.team === closest.team && s.jersey === closest.jersey,
      )
      if (idx >= 0) {
        this.selectedPlayers.splice(idx, 1)
      } else {
        if (this.selectedPlayers.length >= MAX_CONNECTED) {
          this.selectedPlayers.shift() // remove oldest
        }
        this.selectedPlayers.push(closest)
      }
      this.setConnectMode(this.selectedPlayers.length > 0)
      redrawAfterClick()
    }
    // capture phase — fires before spatial canvas's own handlers
    spatialCanvas.addEventListener('click', this.onSpatialClick, true)
  }
}
